import React, { useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "react-toastify";
import { Modal, Button, Form } from "react-bootstrap";
import { IoCloseSharp, IoCropSharp } from "react-icons/io5";
import SwitchToggle from "../Common/SwitchToggle";
import axiosUserInstance from "../../axiosUserInstance";
import { useLoader } from "../../context/LoaderContext";
import { parseRatio, uploadFileToS3 } from "../../utils/common";

export default function AlbumEmbedModal({
  show,
  onClose,
  userData,
  setUserData,
  albumEmbedFormData,
  setAlbumEmbedFormData,
}) {
  const { setLoading } = useLoader();
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [imageMediaError, setImageMediaError] = useState("");
  const [videoMediaError, setVideoMediaError] = useState("");
  const [imageSrc, setImageSrc] = useState(null);
  const [editingImageSrc, setEditingImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  const handleClear = () => {
    if (imageInputRef.current) {
      setImageMediaError("");
      imageInputRef.current.value = "";
    }
    if (videoInputRef.current) {
      setVideoMediaError("");
      videoInputRef.current.value = "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAlbumEmbedFormData({ ...albumEmbedFormData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("image/")) {
      setImageMediaError("Only image allowed");
      imageInputRef.current.value = "";
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      const uploadedRatio = img.width / img.height;
      const expectedRatio = parseRatio(albumEmbedFormData?.ratio);

      // Allow small floating difference
      if (Math.abs(uploadedRatio - expectedRatio) > 0.02) {
        // Show inline cropper to allow user to crop to expected ratio
        setImageMediaError("");
        setImageSrc(img.src);
        setIsCropping(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        // keep the input value so user can cancel or crop
        return;
      }

      if (imageMediaError) setImageMediaError("");
      setAlbumEmbedFormData((prev) => ({
        ...prev,
        originalMediaFile: file,
        mediaType: "image",
        localPreview: img.src,
      }));
    };
  };

  const onCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const getCroppedImg = async (imageSrcArg, cropPixels) => {
    const image = new Image();
    image.src = imageSrcArg;
    await new Promise((resolve) => (image.onload = resolve));

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = cropPixels.width;
    canvas.height = cropPixels.height;

    ctx.drawImage(
      image,
      cropPixels.x,
      cropPixels.y,
      cropPixels.width,
      cropPixels.height,
      0,
      0,
      cropPixels.width,
      cropPixels.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const file = new File([blob], "cropped-image.jpg", {
          type: "image/jpeg",
        });
        resolve(file);
      }, "image/jpeg");
    });
  };

  const handleCropSave = async () => {
    try {
      if (!croppedAreaPixels || !imageSrc) return;
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels);
      const previewUrl = URL.createObjectURL(croppedFile);
      setAlbumEmbedFormData((prev) => {
        try {
          if (prev?.localPreview && String(prev.localPreview).startsWith("blob:")) {
            URL.revokeObjectURL(prev.localPreview);
          }
        } catch (e) {}
        return {
          ...prev,
          originalMediaFile: croppedFile,
          mediaType: "image",
          localPreview: previewUrl,
        };
      });
      // cleanup only the temporary editing URL (if any)
      if (editingImageSrc) {
        URL.revokeObjectURL(editingImageSrc);
        setEditingImageSrc(null);
      } else if (imageSrc && imageSrc.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(imageSrc);
        } catch (e) {}
      }
      setImageSrc(null);
      setIsCropping(false);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (err) {
      console.error(err);
      toast.error("Failed to crop image.");
    }
  };

  const handleCropCancel = () => {
    if (editingImageSrc) {
      try {
        URL.revokeObjectURL(editingImageSrc);
      } catch (e) {}
      setEditingImageSrc(null);
    } else if (imageSrc && imageSrc.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(imageSrc);
      } catch (e) {}
    }
    setImageSrc(null);
    setIsCropping(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const handleEditPreviewImage = () => {
    if (albumEmbedFormData?.originalMediaFile instanceof File) {
      const tempUrl = URL.createObjectURL(albumEmbedFormData.originalMediaFile);
      setEditingImageSrc(tempUrl);
      setImageSrc(tempUrl);
    } else if (albumEmbedFormData?.localPreview) {
      setImageSrc(albumEmbedFormData.localPreview);
    } else if (albumEmbedFormData?.originalMediaLink) {
      setImageSrc(albumEmbedFormData.originalMediaLink);
    }
    setIsCropping(true);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("video/")) {
      setVideoMediaError("Only video allowed");
      videoInputRef.current.value = "";
      return;
    }

    const video = document.createElement("video");
    video.src = URL.createObjectURL(file);
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      const uploadedRatio = video.videoWidth / video.videoHeight;
      const expectedRatio = parseRatio(albumEmbedFormData?.ratio);

      if (Math.abs(uploadedRatio - expectedRatio) > 0.02) {
        setVideoMediaError(`Video must be ${albumEmbedFormData?.ratio} ratio`);
        videoInputRef.current.value = "";
        URL.revokeObjectURL(video.src);
        return;
      }

      if (videoMediaError) setVideoMediaError("");
      setAlbumEmbedFormData((prev) => ({
        ...prev,
        originalMediaFile: file,
        mediaType: "video",
        localPreview: video.src,
      }));
    };
  };

  const submitForm = async () => {
    if (!albumEmbedFormData.originalMediaFile && !albumEmbedFormData.originalMediaLink) {
      setImageMediaError("Please upload media file.");
      return;
    } else {
      if (imageMediaError) setImageMediaError("");
      if (videoMediaError) setVideoMediaError("");
    }

    try {
      setLoading(true);
      let originalMediaLink = albumEmbedFormData.originalMediaLink || "";
      let embedLink = albumEmbedFormData.embedLink || "";

      if (embedLink && !embedLink.startsWith("http://") && !embedLink.startsWith("https://")) {
        embedLink = "https://" + embedLink;
      }
      
      // Upload ONLY original file
      if (albumEmbedFormData.originalMediaFile) {
        originalMediaLink = await uploadFileToS3(
          albumEmbedFormData.originalMediaFile
        );
      }
      const response = await axiosUserInstance.post(
        albumEmbedFormData?._id ? `/widget/edit` : `/widget/add`,
        {
          type: "albumEmbed",
          ...albumEmbedFormData,
          originalMediaLink,
          embedLink,
          _id: albumEmbedFormData?._id || undefined,
        }
      );
      if (response?.status === 200) {
        const data = await response.data?.data;
        setUserData((prev) => ({
          ...prev,
          userWidget: data,
        }));
        setAlbumEmbedFormData({
          lightWeightMediaLink: "",
          originalMediaLink: "",
          mediaType: "",
          isLinkEmbed: false,
          embedLink: "",
          ratio: "1:1",
        });
        onClose();
        toast.success("Album created successfully.");
      } else {
        toast.error(
          response?.message || "Album creation failed. Please try again."
        );
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred. Please try again.";
      toast.error(apiError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      backdrop="static"
      className="countdown-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="mb-0">
          {albumEmbedFormData?.ratio} Panel
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {(albumEmbedFormData?.localPreview ||
            albumEmbedFormData?.originalMediaLink ||
            (isCropping && imageSrc)) && (
            <div className="mb-3">
              <p className="mb-1">Uploaded file preview:</p>
              {isCropping && imageSrc ? (
                <div>
                  <div
                    style={{
                      position: "relative",
                      height: 400,
                      background: "#111",
                      borderRadius: "8px",
                      marginBottom: "12px",
                    }}
                  >
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={parseRatio(albumEmbedFormData?.ratio)}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                    <div className="flex-grow-1">
                      <label style={{ marginRight: 8, display: "block", marginBottom: "8px" }}>
                        Zoom
                      </label>
                      <input
                        type="range"
                        min={1}
                        max={3}
                        step={0.1}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        style={{ width: "100%" }}
                      />
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCropSave}
                    >
                      Save Crop
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCropCancel}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="position-relative">
                  <button
                    onClick={() => {
                      if (albumEmbedFormData.localPreview) {
                        URL.revokeObjectURL(albumEmbedFormData.localPreview);
                      }
                      setAlbumEmbedFormData((prev) => ({
                        ...prev,
                        localPreview: null,
                        originalMediaFile: null,
                        lightWeightMediaLink: null,
                        originalMediaLink: null,
                      }));
                      handleClear();
                    }}
                    type="button"
                    className="position-absolute top-0 end-0 bg-white rounded-circle shadow"
                    style={{
                      width: "24px",
                      height: "24px",
                      border: "none",
                      zIndex: 99,
                    }}
                    aria-label="Close"
                  >
                    <IoCloseSharp />
                  </button>
                  <div className="object-fit-content">
                    {albumEmbedFormData?.mediaType === "video" ? (
                      albumEmbedFormData?.localPreview ? (
                        <video
                          src={albumEmbedFormData?.localPreview}
                          muted
                          autoPlay
                          loop
                          playsInline
                          controls={false}
                          className="rounded"
                          style={{ maxWidth: "100%", maxHeight: "100%" }}
                        />
                      ) : (
                        <video width="100%" height="auto" autoPlay loop controls>
                          <source
                            src={albumEmbedFormData?.originalMediaLink}
                            type={albumEmbedFormData?.type}
                          />
                        </video>
                      )
                    ) : (
                      <>
                        <img
                          src={
                            albumEmbedFormData?.localPreview ||
                            albumEmbedFormData?.originalMediaLink
                          }
                          alt="Album preview"
                          className="object-fit"
                        />
                        {albumEmbedFormData?.mediaType === "image" && (
                          <button
                            onClick={handleEditPreviewImage}
                            type="button"
                            className="btn btn-sm btn-primary"
                            style={{
                              position: "absolute",
                              top: "8px",
                              left: "8px",
                              zIndex: 50,
                              padding: "4px 6px",
                              borderRadius: "4px",
                              width: "fit-content",
                            }}
                          >
                            <IoCropSharp size={20} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="mb-3">
            <label>Add Image Preview</label>
            <Form.Control
              type="file"
              accept="image/*"
              ref={imageInputRef}
              onChange={handleImageChange}
            />
            {imageMediaError && (
              <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                {imageMediaError}
              </div>
            )}
          </div>
          {userData?.planType !== "free" && (
            <div className="mb-3">
              <label>Add Video Preview</label>
              <Form.Control
                type="file"
                accept="video/*"
                ref={videoInputRef}
                onChange={handleVideoChange}
              />
              {videoMediaError && (
                <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                  {videoMediaError}
                </div>
              )}
            </div>
          )}
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center">
              <label
                className="me-2"
                style={{ fontSize: "14px", whiteSpace: "nowrap" }}
              >
                Link Embed
              </label>
              <SwitchToggle
                checked={albumEmbedFormData?.isLinkEmbed}
                onChange={() => {
                  if (albumEmbedFormData?.mediaType === "video") {
                    if (userData?.planType === "yearly") {
                      setAlbumEmbedFormData((prev) => ({
                        ...prev,
                        isLinkEmbed: !prev.isLinkEmbed,
                      }));
                    } else {
                      toast.info(`Yearly plan is allow to unable embed link.`);
                    }
                  } else if (albumEmbedFormData?.mediaType === "image") {
                    if (userData?.planType !== "free") {
                      setAlbumEmbedFormData((prev) => ({
                        ...prev,
                        isLinkEmbed: !prev.isLinkEmbed,
                      }));
                    } else {
                      toast.info(
                        `Free plan is not allow to unable embed link.`
                      );
                    }
                  } else {
                    toast.info(`First of upload image or video.`);
                  }
                }}
              />
            </div>
            <div className="w-100">
              <Form.Control
                type="text"
                name="embedLink"
                placeholder="Ex. https://biomeee.com/"
                value={albumEmbedFormData.embedLink}
                onChange={handleChange}
                disabled={!albumEmbedFormData.isLinkEmbed}
              />
            </div>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={submitForm}>
          {albumEmbedFormData?._id ? `Update` : `Save`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
